import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogFormGrid, DialogFormSection } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { benefitsAPI, employeeAPI, workspaceAdminAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { hasAnyRole } from "../lib/rbac";
import { Loader2, Plus, Heart, Shield, Umbrella, HeartHandshake, ShieldCheck, Eye, Pencil, Trash2 } from "lucide-react";
import Swal from 'sweetalert2';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { TableExportActions } from './ui/table-export-actions';
import { chartColor, tooltipContentStyle } from '../lib/chartTheme';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const BenefitsManagement = ({ currentTab = "plans", hiddenTabs = [], scope = "tenant" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(currentTab);
  const [loading, setLoading] = useState(false);
  const isAdminOrManager = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin', 'manager', 'hr_manager']);
  // The superadmin master screen edits the template new workspaces are seeded
  // with, not any one tenant's plans — a tenant's own records are reachable
  // only inside a proxy session, which is why the tenant endpoint refuses here.
  const isGlobalScope = scope === "global";

  useEffect(() => {
    if (currentTab) {
      const nextTab = currentTab === "enrollment" && !isAdminOrManager
        ? "my-benefits"
        : currentTab;
      setActiveTab(nextTab);
    }
  }, [currentTab, isAdminOrManager]);

  const isTabVisible = (tabId) => !hiddenTabs.includes(tabId);
  
  // Data States
  const [plans, setPlans] = useState([]);
  const [myBenefits, setMyBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  
  // Dialog States
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);

  // Form States
  const [newPlan, setNewPlan] = useState({
    name: "", type: "health", description: "", coverage_amount: 0,
    premium_amount: 0, employer_contribution: 0, employee_contribution: 0
  });
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [viewPlan, setViewPlan] = useState(null);

  const [enrollment, setEnrollment] = useState({
    employee_id: "", benefit_plan_id: "", coverage_amount: 0,
    employee_contribution: 0, employer_contribution: 0,
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (activeTab === "my-benefits") fetchMyBenefits();
    if (activeTab === "plans") fetchPlans();
    if (activeTab === "enrollment" && isAdminOrManager) { fetchPlans(); fetchEmployees(); fetchEnrollments(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdminOrManager, isGlobalScope]);

  const fetchPlans = async () => {
    try {
      const response = isGlobalScope
        ? await workspaceAdminAPI.listGlobalBenefitPlans()
        : await benefitsAPI.getPlans();
      const planList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setPlans(planList);
    } catch (error) {
      setPlans([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch benefit plans',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await benefitsAPI.getEnrollments();
      const enrollmentList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setEnrollments(enrollmentList);
    } catch (error) {
      setEnrollments([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch enrollments',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  const fetchMyBenefits = async () => {
    try {
      const response = await benefitsAPI.getMyBenefits();
      const benefitList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setMyBenefits(benefitList);
    } catch (error) {
      setMyBenefits([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch your benefits',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.list();
      const employeeList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setEmployees(employeeList);
    } catch (error) {
      setEmployees([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch employees',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  const resetPlanForm = () => {
    setEditingPlanId(null);
    setNewPlan({ name: "", type: "health", description: "", coverage_amount: 0, premium_amount: 0, employer_contribution: 0, employee_contribution: 0 });
  };

  const openAddPlan = () => { resetPlanForm(); setShowPlanDialog(true); };

  const openEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setNewPlan({
      name: plan.name ?? "",
      type: plan.type ?? "health",
      description: plan.description ?? "",
      coverage_amount: plan.coverage_amount ?? 0,
      premium_amount: plan.premium_amount ?? 0,
      employer_contribution: plan.employer_contribution ?? 0,
      employee_contribution: plan.employee_contribution ?? 0,
    });
    setShowPlanDialog(true);
  };

  const handleDeletePlan = async (plan) => {
    const result = await Swal.fire({
      icon: 'warning', title: `Delete "${plan.name}"?`, text: 'This cannot be undone.',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      if (isGlobalScope) {
        await workspaceAdminAPI.deleteGlobalBenefitPlan(plan.id);
      } else {
        await benefitsAPI.deletePlan(plan.id);
      }
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Benefit plan deleted', confirmButtonColor: 'hsl(var(--primary))' });
      fetchPlans();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.detail || 'Failed to delete plan', confirmButtonColor: 'hsl(var(--primary))' });
    }
  };

  const handleCreatePlan = async () => {
    try {
      if (editingPlanId && isGlobalScope) {
        await workspaceAdminAPI.updateGlobalBenefitPlan(editingPlanId, newPlan);
      } else if (editingPlanId) {
        await benefitsAPI.updatePlan(editingPlanId, newPlan);
      } else if (isGlobalScope) {
        await workspaceAdminAPI.createGlobalBenefitPlan(newPlan);
      } else {
        await benefitsAPI.createPlan(newPlan);
      }
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: editingPlanId ? 'Benefit Plan Updated' : 'Benefit Plan Created',
        confirmButtonColor: 'hsl(var(--primary))'
      });
      setShowPlanDialog(false);
      resetPlanForm();
      fetchPlans();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: editingPlanId ? 'Failed to update plan' : 'Failed to create plan',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  const handleEnroll = async () => {
    try {
      await benefitsAPI.enroll(enrollment);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Employee Enrolled Successfully',
        confirmButtonColor: 'hsl(var(--primary))'
      });
      setShowEnrollDialog(false);
      fetchEnrollments();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to enroll employee',
        confirmButtonColor: 'hsl(var(--primary))'
      });
    }
  };

  // Helper to auto-fill contributions when plan is selected
  const handlePlanSelect = (planId) => {
    const plan = plans.find(p => p.id.toString() === planId);
    if (plan) {
      setEnrollment({
        ...enrollment,
        benefit_plan_id: planId,
        coverage_amount: plan.coverage_amount,
        employee_contribution: plan.employee_contribution,
        employer_contribution: plan.employer_contribution
      });
    }
  };

  const benefitsByType = useMemo(() => {
    const counts = myBenefits.reduce((acc, item) => {
      const type = (item.plan?.type || 'other').toLowerCase();
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value], idx) => ({
      name,
      value,
      color: chartColor(idx),
    }));
  }, [myBenefits]);

  const activeBenefits = useMemo(
    () => myBenefits.filter((item) => String(item.status || '').toLowerCase() === 'active').length,
    [myBenefits]
  );

  const plansExportColumns = useMemo(
    () => [
      { label: 'Name', accessor: (row) => row.name },
      { label: 'Type', accessor: (row) => row.type },
      { label: 'Coverage', accessor: (row) => row.coverage_amount },
      { label: 'Premium', accessor: (row) => row.premium_amount },
      { label: 'Employee Contribution', accessor: (row) => row.employee_contribution },
      { label: 'Employer Contribution', accessor: (row) => row.employer_contribution },
    ],
    []
  );

  const enrollmentsExportColumns = useMemo(
    () => [
      { label: 'Employee', accessor: (row) => row.employee_id },
      { label: 'Plan Name', accessor: (row) => row.plan_name },
      { label: 'Plan Type', accessor: (row) => row.plan_type },
      { label: 'Start Date', accessor: (row) => row.start_date },
      { label: 'Coverage', accessor: (row) => row.coverage_amount },
      { label: 'Status', accessor: (row) => row.status },
    ],
    []
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-hr-h2 font-bold tracking-tight">Benefits Administration</h2>
          <p className="text-hr-sm text-muted-foreground">Manage insurance plans, retirement benefits, and enrollments.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="sticky top-2 z-20 border border-border bg-background">
          {isTabVisible("my-benefits") && (
            <TabsTrigger value="my-benefits" className="text-hr-sm gap-1.5"><Heart className="h-3.5 w-3.5" />My Benefits</TabsTrigger>
          )}
          {isTabVisible("plans") && (
            <TabsTrigger value="plans" className="text-hr-sm gap-1.5"><Shield className="h-3.5 w-3.5" />Benefit Plans</TabsTrigger>
          )}
          {isAdminOrManager && isTabVisible("enrollment") && (
            <TabsTrigger value="enrollment" className="text-hr-sm gap-1.5"><Umbrella className="h-3.5 w-3.5" />Enrollment</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-benefits" className="space-y-4 animate-slide-up">
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard label="Total Benefits" value={myBenefits.length} icon={HeartHandshake} tone="primary" />
            <StatCard label="Active" value={activeBenefits} icon={ShieldCheck} tone="success" />
            <StatCard label="Plans Available" value={plans.length} icon={Shield} tone="warning" />
          </div>

          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-hr-lg">Benefits by Type</CardTitle>
            </CardHeader>
            <CardContent className="h-[280px]">
              {benefitsByType.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={benefitsByType}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={95}
                    >
                      {benefitsByType.map((entry, idx) => (
                        <Cell key={entry.name} fill={chartColor(idx)} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipContentStyle} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <EmptyState compact title="No benefits yet" description="Your enrolled benefits will appear here." icon={HeartHandshake} />
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {myBenefits.map((benefit, index) => (
              <Card key={benefit.id} className="hover-lift transition-all duration-200 border-border shadow-sm hover:shadow-md" style={{ animationDelay: `${index * 0.05}s` }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-hr-sm font-medium">
                    {benefit.plan?.name || "Benefit Plan"}
                  </CardTitle>
                  {benefit.plan?.type === 'health' ? <Heart className="h-4 w-4 text-destructive" /> : 
                   benefit.plan?.type === 'retirement' ? <Umbrella className="h-4 w-4 text-primary" /> : 
                   <Shield className="h-4 w-4 text-[hsl(var(--chart-2))]" />}
                </CardHeader>
                <CardContent>
                  <div className="text-hr-h2 font-bold">{benefit.coverage_amount ? `Coverage: ${benefit.coverage_amount.toLocaleString()}` : "Active"}</div>
                  <p className="text-hr-xs text-muted-foreground">
                    Employee Contrib: {benefit.employee_contribution} | Employer: {benefit.employer_contribution}
                  </p>
                  <Badge className="mt-2" variant="outline">{benefit.status}</Badge>
                </CardContent>
              </Card>
            ))}
            {myBenefits.length === 0 && (
              <div className="col-span-3">
                <EmptyState compact title="No active benefits" description="No active benefits found. Contact HR for enrollment." icon={HeartHandshake} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="plans" className="space-y-4 animate-slide-up">
          <div className="flex justify-between">
            <h3 className="text-hr-lg font-medium">Available Plans</h3>
            {isAdminOrManager && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/admin/import/benefit_plan')}>Import Plans</Button>
              <Dialog open={showPlanDialog} onOpenChange={(o) => { setShowPlanDialog(o); if (!o) resetPlanForm(); }}>
                <DialogTrigger asChild>
                  <Button onClick={openAddPlan}><Plus className="mr-2 h-4 w-4" /> Create Plan</Button>
                </DialogTrigger>
              <DialogContent size="lg">
                <DialogHeader>
                  <DialogTitle>{editingPlanId ? 'Edit Benefit Plan' : 'Create Benefit Plan'}</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Plan Name</Label>
                      <Input value={newPlan.name} onChange={e => setNewPlan({...newPlan, name: e.target.value})} placeholder="e.g. Gold Health Plan" />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select onValueChange={val => setNewPlan({...newPlan, type: val})} defaultValue={newPlan.type}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="health">Health Insurance</SelectItem>
                          <SelectItem value="retirement">Retirement/Pension</SelectItem>
                          <SelectItem value="wellness">Wellness</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </DialogFormGrid>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input value={newPlan.description} onChange={e => setNewPlan({...newPlan, description: e.target.value})} />
                  </div>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Coverage Amount</Label>
                      <Input type="number" value={newPlan.coverage_amount} onChange={e => setNewPlan({...newPlan, coverage_amount: parseFloat(e.target.value)})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Premium Amount</Label>
                      <Input type="number" value={newPlan.premium_amount} onChange={e => setNewPlan({...newPlan, premium_amount: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Employee Contribution</Label>
                      <Input type="number" value={newPlan.employee_contribution} onChange={e => setNewPlan({...newPlan, employee_contribution: parseFloat(e.target.value)})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Employer Contribution</Label>
                      <Input type="number" value={newPlan.employer_contribution} onChange={e => setNewPlan({...newPlan, employer_contribution: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                </DialogFormSection>
                <DialogFooter>
                  <Button onClick={handleCreatePlan}>{editingPlanId ? 'Update Plan' : 'Create Plan'}</Button>
                </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            )}
          </div>
          <div className="flex justify-end">
            <TableExportActions
              rows={plans}
              columns={plansExportColumns}
              fileName="benefit_plans"
              title="Benefit Plans"
            />
          </div>
          <Table className="text-hr-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-hr-sm text-muted-foreground">Name</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Type</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Coverage</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Premium</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Contributions (Emp/Employer)</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell className="font-medium text-hr-sm">{plan.name}</TableCell>
                  <TableCell className="capitalize text-hr-sm">{plan.type}</TableCell>
                  <TableCell className="text-hr-sm">{plan.coverage_amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">{plan.premium_amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">{plan.employee_contribution} / {plan.employer_contribution}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" title="View" onClick={() => setViewPlan(plan)}><Eye className="h-4 w-4" /></Button>
                    {isAdminOrManager && (
                      <>
                        <Button variant="ghost" size="icon" title="Edit" onClick={() => openEditPlan(plan)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" title="Delete" onClick={() => handleDeletePlan(plan)}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* View benefit plan (read-only) */}
          <Dialog open={!!viewPlan} onOpenChange={(o) => !o && setViewPlan(null)}>
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>Benefit Plan Details</DialogTitle>
              </DialogHeader>
              {viewPlan && (
                <div className="space-y-2 py-2 text-hr-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{viewPlan.name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="capitalize">{viewPlan.type}</span></div>
                  {viewPlan.provider && <div className="flex justify-between"><span className="text-muted-foreground">Provider</span><span>{viewPlan.provider}</span></div>}
                  {viewPlan.description && <div><span className="text-muted-foreground">Description</span><div>{viewPlan.description}</div></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Employee Contribution</span><span>{viewPlan.employee_contribution}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Employer Contribution</span><span>{viewPlan.employer_contribution}</span></div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {isAdminOrManager && (
        <TabsContent value="enrollment" className="space-y-4 animate-slide-up">
          <div className="flex justify-between">
             <h3 className="text-hr-lg font-medium">Employee Enrollment</h3>
             <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Enroll Employee</Button>
              </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader>
                  <DialogTitle>Enroll Employee</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                   <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select onValueChange={val => setEnrollment({...enrollment, employee_id: val})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.employee_id} value={emp.employee_id}>
                              {emp.first_name} {emp.last_name} ({emp.employee_id})
                            </SelectItem>
                          ))}
                        </SelectContent>
                     </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Benefit Plan</Label>
                    <Select onValueChange={handlePlanSelect}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(p => (
                            <SelectItem key={p.id} value={p.id.toString()}>
                              {p.name} ({p.type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                     </Select>
                  </div>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input type="date" value={enrollment.start_date} onChange={e => setEnrollment({...enrollment, start_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Coverage Amount</Label>
                      <Input type="number" value={enrollment.coverage_amount} onChange={e => setEnrollment({...enrollment, coverage_amount: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                </DialogFormSection>
                <DialogFooter>
                  <Button onClick={handleEnroll}>Confirm Enrollment</Button>
                </DialogFooter>
              </DialogContent>
             </Dialog>
          </div>
          <div className="flex justify-end">
            <TableExportActions
              rows={enrollments}
              columns={enrollmentsExportColumns}
              fileName="benefit_enrollments"
              title="Benefit Enrollments"
            />
          </div>
          <Table className="text-hr-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-hr-sm text-muted-foreground">Employee</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Plan Name</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Type</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Start Date</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Coverage</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollments.map((enroll) => (
                <TableRow key={enroll.id}>
                  <TableCell className="font-medium text-hr-sm">{enroll.employee_id}</TableCell>
                  <TableCell className="text-hr-sm">{enroll.plan_name}</TableCell>
                  <TableCell className="capitalize text-hr-sm">{enroll.plan_type}</TableCell>
                  <TableCell className="text-hr-sm">{enroll.start_date}</TableCell>
                  <TableCell className="text-hr-sm">{enroll.coverage_amount?.toLocaleString()}</TableCell>
                  <TableCell><Badge variant="outline">{enroll.status}</Badge></TableCell>
                </TableRow>
              ))}
              {enrollments.length === 0 && (
                 <TableRow>
                  <TableCell colSpan={6} className="p-4">
                    <EmptyState compact title="No enrollments yet" description="No active enrollments found." icon={ShieldCheck} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default BenefitsManagement;
